/**
 * DobInput — date-of-birth input with both **free-type** and **calendar**
 * affordances. Drop-in replacement for `<Input type="date" />`.
 *
 *   <DobInput
 *     value={"1990-04-23"}          // ISO YYYY-MM-DD (same as <input type=date/>)
 *     onChange={(iso) => …}         // called with ISO or "" when cleared
 *     placeholder="MM/DD/YYYY"
 *     min="1900-01-01"
 *     max="2010-12-31"
 *     data-testid="dob"
 *   />
 *
 * UX:
 *   • Type "12251990" → auto-formats as you go to "12/25/1990".
 *   • Backspace works naturally (slashes are skipped).
 *   • On blur, validates MM/DD/YYYY and emits ISO. Invalid → leaves the
 *     raw text in place + sets `aria-invalid="true"` for screen readers.
 *   • Calendar icon button on the right opens a shadcn `<Popover />`
 *     with the same `<Calendar />` we use everywhere else. Selecting a
 *     date fills the text input AND emits the ISO value.
 *   • Keyboard accessible — Enter inside the input commits the typed
 *     value, Esc closes the popover.
 */
import { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ---------- helpers ----------
const DIGITS = (s) => String(s || "").replace(/\D/g, "");

function isoToDisplay(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  return `${m[2]}/${m[3]}/${m[1]}`;
}

function displayToIso(text) {
  if (!text) return "";
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
  if (!m) return "";
  const mm = parseInt(m[1], 10);
  const dd = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return "";
  if (dd < 1 || dd > 31) return "";
  // JS Date catches "Feb 31" by overflowing → reject those
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return "";
  if (yyyy < 1900 || yyyy > 2100) return "";
  return `${yyyy.toString().padStart(4, "0")}-${mm.toString().padStart(2, "0")}-${dd.toString().padStart(2, "0")}`;
}

/** Live-format raw digits → MM/DD/YYYY as the user types. */
function formatMask(raw) {
  const d = DIGITS(raw).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// ---------- component ----------
export default function DobInput({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  min,
  max,
  disabled,
  className,
  "data-testid": testId,
  ...rest
}) {
  const [text, setText] = useState(isoToDisplay(value));
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef(null);

  // External value can change (form reset, async load) — sync text down.
  useEffect(() => {
    setText(isoToDisplay(value));
    setInvalid(false);
  }, [value]);

  const handleTextChange = (e) => {
    const next = formatMask(e.target.value);
    setText(next);
    setInvalid(false);
    // Emit ISO upstream as soon as we have a valid full date — keeps form
    // state in sync without forcing the user to blur.
    const iso = displayToIso(next);
    if (iso) onChange?.(iso);
    else if (!next) onChange?.("");
  };

  const handleBlur = () => {
    if (!text) { setInvalid(false); onChange?.(""); return; }
    const iso = displayToIso(text);
    if (!iso) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange?.(iso);
  };

  const handleCalendarPick = (date) => {
    if (!date) return;
    const iso = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    setText(isoToDisplay(iso));
    setInvalid(false);
    onChange?.(iso);
    setPopoverOpen(false);
    // Return focus to the input for keyboard flow.
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const fromDateMin = min ? new Date(min + "T00:00:00") : new Date("1900-01-01T00:00:00");
  const fromDateMax = max ? new Date(max + "T00:00:00") : new Date();

  return (
    <div className={cn("relative", className)} data-testid={testId ? `${testId}-wrap` : undefined}>
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder={placeholder}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          "pr-10",
          invalid && "border-rose-500 focus-visible:ring-rose-300",
        )}
        data-testid={testId}
        {...rest}
      />
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open calendar"
            disabled={disabled}
            onClick={() => setPopoverOpen((v) => !v)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors disabled:opacity-50"
            data-testid={testId ? `${testId}-calendar-btn` : "dob-calendar-btn"}
          >
            <CalendarIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="p-0 w-auto">
          <Calendar
            mode="single"
            selected={value ? new Date(value + "T00:00:00") : undefined}
            onSelect={handleCalendarPick}
            fromDate={fromDateMin}
            toDate={fromDateMax}
            captionLayout="dropdown-buttons"
            fromYear={fromDateMin.getFullYear()}
            toYear={fromDateMax.getFullYear()}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {invalid && (
        <p className="mt-1 text-[11px] text-rose-500" data-testid={testId ? `${testId}-error` : undefined}>
          Enter a valid date in MM/DD/YYYY format
        </p>
      )}
    </div>
  );
}
